import React, { useEffect } from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';

// Swallow noisy browser-extension errors (e.g. MetaMask) so React's dev overlay
// doesn't hijack the screen. These errors originate from injected extension
// scripts (chrome-extension://...) and are not caused by our app.
const EXTENSION_ERROR_PATTERNS = [
  /MetaMask/i,
  /chrome-extension:\/\//i,
  /moz-extension:\/\//i,
  /inpage\.js/i,
];
const isExtensionError = (msg = '', source = '') => {
  const text = `${msg} ${source}`;
  return EXTENSION_ERROR_PATTERNS.some((r) => r.test(text));
};
import { AuthProvider } from './contexts/AuthContext';
import { Toaster } from './components/ui/sonner';
import Landing from './pages/Landing';
import CommissionAlerts from './components/CommissionAlerts';
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Dashboard from './pages/Dashboard';
import Memberships from './pages/Memberships';
import Pricing from './pages/Pricing';
import SalesSubmit from './pages/SalesSubmit';
import Admin from './pages/Admin';
import AdminPanel from './pages/AdminPanel';
import UploadMilestoneVideo from './pages/UploadMilestoneVideo';
import AddAdvertiser from './pages/AddAdvertiser';
import AddUser from './pages/AddUser';
import PaymentSuccess from './pages/PaymentSuccess';
import PaymentCancel from './pages/PaymentCancel';
import AIVideoBuilder from './pages/AIVideoBuilder';
import VideoLibrary from './pages/VideoLibrary';
import CommissionStatements from './pages/CommissionStatements';
import Storefront from './pages/Storefront';
import Courses from './pages/Courses';
import CourseDetail from './pages/CourseDetail';
import CourseCreate from './pages/CourseCreate';
import YouTubeVideos from './pages/YouTubeVideos';
import Scheduler from './pages/Scheduler';
import SchedulerSettings from './pages/SchedulerSettings';
import BookingPage from './pages/BookingPage';
import MemberProfile from './pages/MemberProfile';
import ProfileSettings from './pages/ProfileSettings';
import BillingHistory from './pages/BillingHistory';
import CRM from './pages/CRM';
import MyCertificates from './pages/MyCertificates';
import CertificateView from './pages/CertificateView';
import MemberDirectory from './pages/MemberDirectory';
import LegalPage from './pages/LegalPage';
import AIAssistantPage from './pages/AIAssistantPage';
import AIAssistantBubble from './components/AIAssistantBubble';
import LegalAcceptanceModal from './components/LegalAcceptanceModal';
import ForcePasswordChangeModal from './components/ForcePasswordChangeModal';
import SsoCallback from './pages/SsoCallback';
import PublicCard from './pages/PublicCard';
import CookieConsentBanner from './components/CookieConsentBanner';
import { useParams } from 'react-router-dom';

// Tiny inline component — preserves the course id when redirecting from
// the legacy /masterclass/:id URLs to /courses/:id.
function MasterclassRedirect() {
  const { courseId } = useParams();
  return <Navigate to={`/courses/${courseId}`} replace />;
}

function App() {
  // Global filter for browser-extension errors so they don't trigger
  // React's red dev overlay.
  useEffect(() => {
    const onError = (e) => {
      if (isExtensionError(e?.message, e?.filename)) {
        e.preventDefault();
        e.stopImmediatePropagation();
        return false;
      }
    };
    const onRejection = (e) => {
      const reason = e?.reason;
      const msg = typeof reason === 'string' ? reason : reason?.message || '';
      const stack = reason?.stack || '';
      if (isExtensionError(msg, stack)) {
        e.preventDefault();
        e.stopImmediatePropagation();
        return false;
      }
    };
    window.addEventListener('error', onError, true);
    window.addEventListener('unhandledrejection', onRejection, true);
    return () => {
      window.removeEventListener('error', onError, true);
      window.removeEventListener('unhandledrejection', onRejection, true);
    };
  }, []);

  return (
    <AuthProvider>
      <div className="App">
        <BrowserRouter>
          <CommissionAlerts />
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/login" element={<Login />} />
            <Route path="/auth/sso-callback" element={<SsoCallback />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/home" element={<Navigate to="/dashboard" replace />} />
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/memberships" element={<Memberships />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/sales" element={<SalesSubmit />} />
            <Route path="/ai-video-builder" element={<AIVideoBuilder />} />
            <Route path="/video-library" element={<VideoLibrary />} />
            <Route path="/statements" element={<CommissionStatements />} />
            <Route path="/courses" element={<Courses />} />
            {/* Legacy /masterclass URLs — redirect to the new /courses
                pages to keep any bookmarks / external links working. */}
            <Route path="/masterclass" element={<Navigate to="/courses" replace />} />
            <Route path="/masterclass/create" element={<Navigate to="/courses/create" replace />} />
            <Route path="/masterclass/:courseId" element={<MasterclassRedirect />} />
            <Route path="/courses/create" element={<CourseCreate />} />
            <Route path="/courses/new" element={<CourseCreate />} />
            <Route path="/courses/:courseId/edit" element={<CourseCreate />} />
            <Route path="/courses/:courseId" element={<CourseDetail />} />
            <Route path="/youtube-videos" element={<YouTubeVideos />} />
            <Route path="/scheduler" element={<Scheduler />} />
            <Route path="/scheduler/settings" element={<SchedulerSettings />} />
            <Route path="/book/:providerId" element={<BookingPage />} />
            <Route path="/member/:userId" element={<MemberProfile />} />
            <Route path="/store/:userId" element={<Storefront />} />
            <Route path="/card/:userId" element={<PublicCard />} />
            <Route path="/profile/settings" element={<ProfileSettings />} />
            <Route path="/billing" element={<BillingHistory />} />
            <Route path="/crm" element={<CRM />} />
            <Route path="/directory" element={<MemberDirectory />} />
            <Route path="/my-certificates" element={<MyCertificates />} />
            <Route path="/certificate/:certId" element={<CertificateView />} />
            <Route path="/admin" element={<AdminPanel />} />
            <Route path="/admin/upload-milestone" element={<UploadMilestoneVideo />} />
            <Route path="/admin/add-advertiser" element={<AddAdvertiser />} />
            <Route path="/admin/add-user" element={<AddUser />} />
            <Route path="/payment/success" element={<PaymentSuccess />} />
            <Route path="/payment/cancel" element={<PaymentCancel />} />
            <Route path="/legal/:slug" element={<LegalPage />} />
            <Route path="/ai-assistant" element={<AIAssistantPage />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <LegalAcceptanceModal />
          <ForcePasswordChangeModal />
          <AIAssistantBubble />
          <CookieConsentBanner />
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </div>
    </AuthProvider>
  );
}

export default App;